/**
 * @file ApplyQuoteDialog.tsx
 * @description Применение рассчитанного тарифа к отгрузке (phase19.12 шаг 5).
 *
 * Решение пользователя: применение расчёта НЕ меняет сумму заказа (F26).
 * Цену для клиента назначает вкладка «Доставка» — там она уходит клиенту на
 * согласование. С phase19.24 шага 5.2 диалог обслуживает ТОЛЬКО отгрузки:
 * выключенный чекбокс «обновить стоимость в заказе» убран, чтобы не намекать
 * на второй путь назначения цены.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  createShipment,
  updateShipment,
  type OrderDeliveryLineDto,
  type ShipmentDto,
} from "../../lib/api-orders";
import type { DeliveryQuoteOption } from "../../lib/api-shop-carriers";
import {
  formatRub,
  formatWeight,
  packagesToInput,
  type DeliveryQuoteFormValue,
} from "../../lib/delivery-quote";
import { tariffDeliveryMode } from "../../lib/delivery-quote-meta";

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  value: DeliveryQuoteFormValue;
  option: DeliveryQuoteOption;
  items: OrderDeliveryLineDto[];
  /** Отгрузки-черновики: в них ещё можно менять тариф и места. */
  drafts: ShipmentDto[];
  defaultTargetId?: string | null;
  /** Цена, которую клиент видел на витрине. */
  clientPrice?: number | null;
  /** Вес, посчитанный сервером по позициям — им закрываем пустые места. */
  autoWeightG?: number | null;
  onApplied: () => void;
}

export function ApplyQuoteDialog({
  open,
  onClose,
  orderId,
  value,
  option,
  items,
  drafts,
  defaultTargetId,
  clientPrice,
  autoWeightG,
  onApplied,
}: Props) {
  const notify = useNotify();
  const [target, setTarget] = useState<string>(defaultTargetId ?? "new");
  const [saving, setSaving] = useState(false);

  const packages = packagesToInput(value.packages, autoWeightG ?? 0);
  const weight = packages.reduce((sum, p) => sum + p.weightG, 0);
  /** Куда применяем: новая отгрузка или один из черновиков. */
  const targets = [
    { id: "new", label: `Новая отгрузка (${items.length} поз.)` },
    ...drafts.map((s) => ({
      id: s.id,
      label: `Отгрузка от ${new Date(s.createdAt).toLocaleDateString("ru-RU")} · ${s.itemsCount} поз.`,
    })),
  ];
  const priceDelta =
    clientPrice === null || clientPrice === undefined
      ? null
      : Math.round((option.price - clientPrice) * 100) / 100;

  async function onSubmit(): Promise<void> {
    setSaving(true);
    const delivery = {
      pickupPointCode: value.mode === "PVZ" ? value.pickupPointCode.trim() || null : null,
      tariffCode: option.tariffCode,
      tariffName: option.tariffName,
      deliveryMode: tariffDeliveryMode(option),
      carrierPrice: option.carrierPrice,
      tariffQuotedAt: new Date().toISOString(),
      tariffSource: "CALCULATED" as const,
      packages,
    };
    try {
      if (target === "new") {
        await createShipment(orderId, {
          ...delivery,
          carrier: "SDEK",
          items: items.map((i) => ({
            orderItemId: i.orderItemId,
            quantity: String(i.quantity),
          })),
        });
      } else {
        await updateShipment(target, delivery);
      }
      notify.success("Тариф применён к отгрузке");
      onApplied();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось применить расчёт");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="max-w-md">
        <DialogHeader>
          <DialogTitle>Применить расчёт</DialogTitle>
          <DialogDescription>
            Тариф и места сохранятся в отгрузку. Сумма заказа не изменится.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 text-sm">
          <div className="flex flex-col gap-1 rounded-lg p-3 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
            <div className="font-medium">
              {option.tariffCode} · {option.tariffName}
            </div>
            <div className="text-on-surface-variant">
              {value.mode === "PVZ"
                ? `В пункт выдачи${value.pickupPointCode ? ` ${value.pickupPointCode}` : ""}`
                : "Курьером до двери"}
              {value.city ? ` · ${value.city}` : ""}
            </div>
            <div className="text-on-surface-variant">
              Мест: {packages.length} · {formatWeight(weight)}
            </div>
            <div>
              Клиенту {formatRub(option.price)} · СДЭК {formatRub(option.carrierPrice)}
            </div>
          </div>

          {priceDelta !== null && priceDelta !== 0 ? (
            <Alert severity="warning" title="Цена изменилась">
              Клиент видел {formatRub(clientPrice ?? 0)}, сейчас {formatRub(option.price)} (
              {priceDelta > 0 ? "+" : "−"}
              {formatRub(Math.abs(priceDelta))}).
            </Alert>
          ) : null}

          <fieldset className="flex flex-col gap-1" disabled={saving}>
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
              Куда применить
            </legend>
            {targets.map((t) => (
              <label key={t.id} className="flex min-h-11 items-center gap-2">
                <input
                  type="radio"
                  name="apply-target"
                  checked={target === t.id}
                  onChange={() => setTarget(t.id)}
                />
                {t.label}
              </label>
            ))}
          </fieldset>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={saving}>
            {saving ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : null}
            Применить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
